'use client';

import React from 'react';
import { LiveKitRoom } from '@livekit/components-react';
import '@livekit/components-styles';
import { useTranslation } from 'next-i18next';
import { TailwindSpinner } from '@components/common/Elements/Spinner';
import { useSwrStartInterview } from '@hooks/useSwrApi/endpoints/interview/useSwrStartInterview';
import { Button } from '@components/common';
import AudioInterviewRoomContent from './AudioInterviewRoomContent';

interface AudioInterviewRoomProps {
  locale: string;
  slug?: string;
  onDisconnect: () => void;
}

/**
 * AudioInterviewRoom
 *
 * This component handles the complete audio interview experience:
 * - Fetches interview data using SWR when component mounts
 * - Shows loading spinner while fetching data
 * - Connects to LiveKit room using fetched token and server URL
 * - Displays current connection state ("Connecting", "Connected", "Disconnected")
 * - Shows a centered, animated Bestie avatar as the interviewer UI
 * - Renders disconnect button
 * - Handles errors and shows toast notifications
 *
 * Props:
 * - locale: Language/locale for the interview
 * - slug: Optional job slug for job-based interview
 * - onDisconnect: Callback when disconnecting from the interview room
 */

const AudioInterviewRoom: React.FC<AudioInterviewRoomProps> = ({
  locale,
  slug,
  onDisconnect,
}) => {
  const { t } = useTranslation('common');

  // SWR hook automatically fetches data when this component mounts
  const {
    data: interviewData,
    isValidating,
  } = useSwrStartInterview({
    locale,
    slug,
    errorCallback: () => {
      onDisconnect();
    },
  });

  // Show loading spinner while fetching
  if (isValidating) {
    return (
      <div className="flex flex-col items-center justify-center space-y-4 py-12">
        <TailwindSpinner className="size-8" />
      </div>
    );
  }

  // If interviewData is missing after isValidating, show an error message
  if (!interviewData || !interviewData.token || !interviewData.livekit_url) {
    return (
      <div className="flex flex-col items-center justify-center space-y-4 py-12">
        <p className="text-center text-sm text-gray-500">{t('global.something.went.wrong')}</p>
        <Button
          color="primary"
          onClick={onDisconnect}
        >
          {t('global.action.back')}
        </Button>
      </div>
    );
  }

  // Render the actual interview room with LiveKit
  return (
    <LiveKitRoom
      token={interviewData.token}
      serverUrl={interviewData.livekit_url}
      audio
      video={false}
      connect
      className="w-full"
    >
      <AudioInterviewRoomContent onDisconnect={onDisconnect} />
    </LiveKitRoom>
  );
};

export default AudioInterviewRoom;
